import { OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { ILoginResponse, IResponse } from '@interfaces/index';
import { Injectable, Inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AppService } from 'projects/access/src/app/services/app.service';
import { AuthService } from 'projects/access/src/app/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class OktaService {
	private _ssoTypeProviderName: string = "okta";
	private _isAuthenticated: boolean = false;
	private _accessToken: any = null;
	private _emailAddress: any = null;
	private _firstName: any = null;
	private _lastName: any = null;

	constructor(
		public appService: AppService,
		public authService: AuthService,
		@Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
		public oktaAuthModule: OktaAuthModule,
		public oktaAuthStateService: OktaAuthStateService,
	) {
		this.setAuthState();
	}

	getIsAuthenticated() {
		return this._isAuthenticated;
	}

	getAccessToken() {
		return this._accessToken;
	}

	getEmailAddress() {
		return this._emailAddress;
	}

	getFirstName() {
		return this._firstName;
	}

	getLastName() {
		return this._lastName;
	}

	getRedirectUrl(): string {
		return (sessionStorage.getItem('oktaRedirectUrl') ?? '');
	}

	setEmailAddress(value: any) {
		this._emailAddress = value;
	}

	setFirstName(value: any) {
		this._firstName = value;
	}

	setLastName(value: any) {
		this._lastName = value;
	}

	setRedirectUrl(redirectUrl: string) {
		sessionStorage.setItem('oktaRedirectUrl', redirectUrl);
	}

	async setAuthState(): Promise<void> {
		const response = await firstValueFrom(this.oktaAuthStateService.authState$);
		this._isAuthenticated = response?.isAuthenticated ?? false;
		this._accessToken = response?.accessToken?.accessToken;
		if (this.getIsAuthenticated()) {
			await new Promise<void>((resolve) => {
				this.getUserDetails((user) => {
					this.setEmailAddress(user.email);
					this.setFirstName(user.given_name);
					this.setLastName(user.family_name);
					resolve();
				});
			});
		}
	}

	getUserDetails(callback: (user: any) => void): void {
		this.oktaAuth.token.getUserInfo().then(callback);
	}

	async signIn() {
		await this.oktaAuth.signInWithRedirect()
	}

	handleLoginResponse(response: any, appService: any) {
		return appService.handleLoginResponse(response);
	}

	async loginUserFromOkta(appServiceCallback?: Function) {
		await this.setAuthState();
		this.authService.setOktaUserAccessToken(
			this.getEmailAddress(),
			this.getAccessToken(),
			this._ssoTypeProviderName
		).subscribe({
			next: (response: IResponse<ILoginResponse>) => {
				if (response?.data) {
					response.data.access_token = this.getAccessToken();
					if (appServiceCallback !== undefined ) {
						this.loginOkta(appServiceCallback);
					}
				} else {
					// account not found (or otherwise rejected) for this Okta identity
					this.appService.handleLoginError(response?.message || 'Login with Okta failed. Please try again.');
				}
			},
			error: () => {
				// handle case where user doesnt exist
				this.appService.handleLoginError('Login with Okta failed. Please try again.');
			}
		});
	}

	async loginOkta(appServiceCallback?: Function) {
		await this.setAuthState();
		this.authService.login(
			this.getEmailAddress(),
			this.getAccessToken(),
			this.getAccessToken()
		).subscribe({
			next:(response: IResponse<ILoginResponse>) => {
				// HttpService turns HTTP errors into a response carrying the API's message,
				// e.g. a disabled (deleted) account
				if (response.code !== 200) {
					this.appService.handleLoginError(response.message || 'Login with Okta failed. Please try again.');
				} else if (appServiceCallback !== undefined) {
					this.appService.handleLoginResponse(response);
				} else {
					window.location.href = this.getRedirectUrl();
				}
			},
			error: () => {
				this.appService.handleLoginError();
			}
		});
	}
}
